import { z } from 'zod';

// slice 02.1 · paso a′ — schemas de las respuestas del servidor del dominio `tasks` y de los
// payloads de escritura. `features/tasks/api.js` corre `.parse()` para que el hook y el
// container reciban data ya validada; los componentes presentacionales nunca corren Zod
// (boundary lint lo prohíbe). Comportamiento-adyacente: una respuesta con forma equivocada
// ahora *lanza* donde antes se renderizaba basura.
//
// La forma real viene de `backend/src/tasks/domain/TaskDTO.js` (allowlist) aplicado sobre
// `formatEventToTaskFields()`. Columnas de la tabla `Task` (2026-09-03): id uuid NOT NULL,
// title text NOT NULL, el resto nullable. `assignee`/`supervisor`/`start_time`/`end_time`/
// `event_type`/`calendar_id` los agrega el JOIN con `CalendarEvent`/`TaskAssignment`;
// `date`/`tiempo`/`start_col`/`duration_cols`/`gantt` los calcula `formatEventToTaskFields`
// (en `ListHitos` no vienen).
//
// `looseObject`: los campos desconocidos pasan tal cual — `ComponentTaskHoy` es un
// god-component que lee muchos campos de forma laxa, un schema que *strippea* borraría data
// en silencio. Solo `id` + `title` son estrictos (identidad + NOT NULL); el resto es
// permisivo a propósito — el objetivo es cazar una forma global equivocada, no tipar cada campo.

const nullish = (schema) => schema.nullish();

export const TaskSchema = z.looseObject({
    id: z.string().min(1),
    title: z.string(),
    description: nullish(z.string()),
    created_by: nullish(z.union([z.number(), z.string()])),
    type: nullish(z.string()),
    status: nullish(z.string()),
    created_at: nullish(z.string()),
    updated_at: nullish(z.string()),
    space_id: nullish(z.union([z.number(), z.string()])),
    checked: nullish(z.boolean()),
    stage: nullish(z.string()),
    priority: nullish(z.union([z.string(), z.number()])),
    event_id: nullish(z.union([z.string(), z.number()])),
    recurrent_interval: nullish(z.union([z.number(), z.string()])),
    recurrent_unit: nullish(z.string()),
    parent_task_id: nullish(z.string()),
    roadmap_y: nullish(z.union([z.number(), z.string()])),
    // JOIN + derivados. `tags`/`assignee`/`supervisor` se omiten a propósito del shape —
    // pasan por `looseObject` como claves desconocidas (forma libre: string, array u objeto).
    start_time: nullish(z.string()),
    end_time: nullish(z.string()),
    event_type: nullish(z.string()),
    calendar_id: nullish(z.union([z.string(), z.number()])),
    date: nullish(z.string()),
    tiempo: nullish(z.string()),
    start_col: nullish(z.number()),
    duration_cols: nullish(z.number()),
    gantt: nullish(z.boolean()),
});

export const TaskListSchema = z.array(TaskSchema);

// Payload de escritura (`createTask` / `updateTask`). Loose a propósito: `ComponentTaskHoy`
// manda ~20 patches parciales distintos (title, stage, checked, priority, tags, space_id,
// recurrencia, date/start_time/end_time/roadmap_y desde el roadmap, …). Solo se tipan `title`
// y `description` (siempre strings donde se mandan); el resto pasa tal cual. El guard real es
// "es un objeto plano con al menos una clave", no la forma de cada campo.
export const TaskWriteSchema = z.looseObject({
    title: z.string().optional(),
    description: z.string().nullish(),
});

// Helpers para `api.js`: parsean solo el sub-árbol de tareas del envelope, dejan el resto
// (`success`, flags de error, etc.) intacto. Si la respuesta no trae tareas, no-op.
export function parseTaskListResponse(body) {
    if (body?.success && Array.isArray(body.tasks)) {
        return { ...body, tasks: TaskListSchema.parse(body.tasks) };
    }
    return body;
}

export function parseTaskResponse(body) {
    if (body?.success && body.task) {
        return { ...body, task: TaskSchema.parse(body.task) };
    }
    return body;
}

// slice 02.1 · paso b′a — schema del wizard de 5 pasos para crear una subtarea (era
// `subtaskFormData`, un objeto a mano sin validación real: presionar Enter con el título vacío
// simplemente no hacía nada, sin mensaje). Sólo `title` es obligatorio; el resto tiene default
// porque el wizard los completa en pasos posteriores (o los deja sin tocar si el usuario no
// asigna encargado/supervisor). `assignee`/`supervisor` guardan el nick elegido del autocomplete,
// no el texto de búsqueda en curso (ese sigue siendo estado transitorio del input, no data del
// form — ver `handleSubtaskKeyDown` en `ComponentTaskHoy`).
export const SubtaskFormSchema = z.object({
    title: z.string().trim().min(1),
    description: z.string().optional().default(''),
    assignee: z.string().nullable().optional().default(null),
    duration: z.number().int().positive().default(30),
    supervisor: z.string().nullable().optional().default(null),
});

// slice 02.1 · paso b′b — schema del form de recordatorio (fecha + usuarios), un popover chico
// con un picker de calendario + multi-select de usuarios (no inputs de texto). El contenedor
// sólo revisa `formState.errors.date`/`.users` (presencia, no el mensaje de Zod) y elige el
// string i18n a mostrar -- el schema no expone sus mensajes a la UI.
export const ReminderFormSchema = z.object({
    date: z.date().nullable().refine((d) => d instanceof Date, 'date_required'),
    users: z.array(z.object({
        id: z.union([z.number(), z.string()]),
        nick: z.string(),
        avatar: z.string().nullable().optional(),
    })).min(1, 'users_required'),
});

// ── Cuadros de trabajo / workframe (`"TaskWorkframe"`, migración 153 — integración main→dev,
// epic D1). `z.looseObject` como el resto: el backend puede sumar campos, el consumidor solo
// necesita id/name/doc_id. ──
export const WorkframeSchema = z.looseObject({
    id: z.union([z.string(), z.number()]),
    space_id: z.union([z.string(), z.number()]).nullish(),
    doc_id: z.string().nullish(),
    name: z.string().nullish(),
    type: z.string().nullish(),
    doc_title: z.string().nullish(),
    doc_icon: z.string().nullish(),
    recent_task_id: z.string().nullish(),
    created_by: z.union([z.string(), z.number()]).nullish(),
    created_at: z.string().nullish(),
});

export const WorkframeListResponseSchema = z.looseObject({
    success: z.boolean(),
    workframes: z.array(WorkframeSchema).nullish(),
    error: z.string().nullish(),
});

export const WorkframeResponseSchema = z.looseObject({
    success: z.boolean(),
    workframe: WorkframeSchema.nullish(),
    error: z.string().nullish(),
});
